import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observable } from "mobx";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  apiGetBillingUsersByUserUuidAccountOptions,
  apiGetBillingUsersByUserUuidAccountQueryKey,
  apiPutBillingUsersUserUuidCredits,
} from "../../../api";
import { RequestError } from "../../../api/api-connector";
import { SetCreditsRequestSchema } from "../../../api/generated/schemas.gen";
import { queryClient } from "../../../api/query-client";
import { errorText } from "../../../app/error-text";
import { Button } from "../../../components/ui-kit/button";
import {
  Dialog,
  DialogBody,
  DialogFooter,
} from "../../../components/ui-kit/dialog";
import { ChooseField } from "../../../components/ui-kit/fields/choose-field";
import { NumberInput } from "../../../components/ui-kit/fields/number-input";
import { TextInput } from "../../../components/ui-kit/fields/text-input";
import { Form } from "../../../components/ui-kit/form/form";
import { FormRow } from "../../../components/ui-kit/form/form-row";
import { FormTable } from "../../../components/ui-kit/form/form-table";
import { RecordDialogTitle } from "../../../components/ui-kit/record-dialog-chrome";
import { Spinner } from "../../../components/ui-kit/spinner";

const BALANCE = SetCreditsRequestSchema.properties.balance;
const NOTE = SetCreditsRequestSchema.properties.note;

/**
 * A screening user's credit balance, loaded when the edit dialog opens and
 * written back on save. `PUT /credits` sets an exact figure and posts the
 * difference to the ledger, which needs a note - so changing the balance
 * asks for the reason.
 */
export function useScreeningCredits(
  /** Null for anyone but a screening user: nothing is loaded or saved. */
  userUuid: string | null,
) {
  const [state] = useState(() =>
    observable<CreditsState>({ balance: undefined, note: "" }),
  );
  const {
    data,
    error,
    isLoading: loading,
  } = useQuery({
    ...apiGetBillingUsersByUserUuidAccountOptions({
      path: { user_uuid: userUuid ?? "" },
    }),
    enabled: userUuid !== null,
    meta: { handlesOwnErrors: true },
  });
  const original = data?.balance;

  async function save() {
    if (
      !userUuid ||
      state.balance === undefined ||
      state.balance === original
    ) {
      return;
    }
    const balance = state.balance;
    await apiPutBillingUsersUserUuidCredits({
      path: { user_uuid: userUuid },
      body: { balance, note: state.note.trim() },
    });
    // The PUT sets this exact figure, so the cached balance can take it as is:
    // reopening the user would otherwise show, and diff against, the old one.
    queryClient.setQueryData(
      apiGetBillingUsersByUserUuidAccountQueryKey({
        path: { user_uuid: userUuid },
      }),
      (old) => old && { ...old, balance },
    );
  }

  return { state, original, error, loading, save };
}

type ScreeningCredits = ReturnType<typeof useScreeningCredits>;

interface CreditsState {
  /** The staged balance; undefined until the user changes it. */
  balance: number | undefined;
  note: string;
}

/**
 * The row at the bottom of the user edit dialog's `FormTable`: the balance
 * with a pencil, like the other pickers there. The modal only stages the new
 * figure and its reason; the dialog's Save writes them.
 */
export const ScreeningCreditsRows = observer(function ScreeningCreditsRows({
  credits: { state, original, error, loading },
  username,
}: {
  credits: ScreeningCredits;
  username: string;
}) {
  const { t } = useLingui();

  if (loading) {
    return <FormRow label={t`Credits left`} data={<Spinner size={20} />} />;
  }

  if (original === undefined) {
    return (
      <FormRow
        label={t`Credits left`}
        data={
          <span className="text-danger-foreground text-sm">
            {error instanceof RequestError && error.response.status === 404 ? (
              <Trans>This user has no billing account.</Trans>
            ) : (
              errorText(error)
            )}
          </span>
        }
      />
    );
  }

  const balance = state.balance ?? original;
  const changed = balance !== original;

  return (
    <FormRow
      label={t`Credits left`}
      data={
        <div className="flex flex-wrap items-center gap-2">
          <ChooseField
            label={changed ? `${original} → ${balance}` : String(original)}
            modal={({ onClose }) => (
              <CreditsModal
                state={state}
                original={original}
                username={username}
                onClose={onClose}
              />
            )}
          />
          {changed && (
            <span className="text-brand-primary text-xs font-semibold">
              <Trans>Unsaved - click Save to apply</Trans>
            </span>
          )}
        </div>
      }
    />
  );
});

const CreditsModal = observer(function CreditsModal({
  state,
  original,
  username,
  onClose,
}: {
  state: CreditsState;
  original: number;
  username: string;
  onClose: () => void;
}) {
  // A copy, so Cancel leaves the staged change as it was.
  const [draft] = useState(() =>
    observable({ balance: state.balance ?? original, note: state.note }),
  );
  const changed = draft.balance !== original;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <RecordDialogTitle>
        <Trans>Change credits: {username}</Trans>
      </RecordDialogTitle>
      <Form
        className="flex min-h-0 flex-1 flex-col"
        onSubmit={() => {
          state.balance = draft.balance;
          state.note = changed ? draft.note : "";
          onClose();
        }}
      >
        <DialogBody>
          <FormTable dataAdd>
            <FormRow label={<Trans>Current credits</Trans>} data={original} />
            <FormRow
              label={<Trans>New amount</Trans>}
              data={
                <NumberInput
                  attr="balance"
                  model={draft}
                  required
                  autofocus
                  min={BALANCE.minimum}
                  max={BALANCE.maximum}
                  step={1}
                  width={120}
                />
              }
              mandatory
            />
            <FormRow
              label={<Trans>Reason for change</Trans>}
              hint={
                <Trans>Saved with the change in the credits history.</Trans>
              }
              data={
                <TextInput
                  attr="note"
                  model={draft}
                  required={changed}
                  minLength={changed ? NOTE.minLength : undefined}
                  maxLength={NOTE.maxLength}
                />
              }
              mandatory={changed}
            />
          </FormTable>
        </DialogBody>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            <Trans>Cancel</Trans>
          </Button>
          <Button variant="primary" type="submit">
            <Trans>Confirm</Trans>
          </Button>
        </DialogFooter>
      </Form>
    </Dialog>
  );
});
